import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, ClipboardPaste, FileUp, Download, Users, CheckCircle2 } from "lucide-react";

export function BulkStudentDialog({ open, onOpenChange, wali, defaultKelas, defaultRombel, onDone }) {
  const [tab, setTab] = useState("paste");
  const [kelas, setKelas] = useState(defaultKelas || "1");
  const [rombel, setRombel] = useState(defaultRombel || "");
  const [password, setPassword] = useState("1234");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const names = text.split("\n").map((l) => l.trim()).filter(Boolean);

  const reset = () => { setText(""); setFile(null); setResult(null); };

  const handleOpenChange = (v) => { if (!v) { reset(); } onOpenChange(v); };

  const downloadTemplate = () => {
    const csv = "Nama,Kelas,Rombel\nAndi Saputra,1,A\nBunga Lestari,1,A\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "template_murid.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const submitPaste = async () => {
    if (names.length === 0) return toast.error("Tempel minimal satu nama murid");
    setSaving(true);
    try {
      const r = await api.post("/students/bulk", {
        students: names.map((name) => ({ name })),
        kelas: parseInt(kelas),
        rombel: rombel.trim().toUpperCase(),
        default_password: password || "1234",
      });
      finishResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menambah murid");
    } finally {
      setSaving(false);
    }
  };

  const submitFile = async () => {
    if (!file) return toast.error("Pilih file Excel (.xlsx) atau CSV dulu");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kelas", String(parseInt(kelas)));
      fd.append("rombel", rombel.trim().toUpperCase());
      fd.append("default_password", password || "1234");
      const r = await api.post("/students/bulk-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      finishResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memproses file");
    } finally {
      setSaving(false);
    }
  };

  const finishResult = (data) => {
    setResult(data);
    toast.success(`${data.count} murid berhasil ditambahkan`);
    onDone && onDone();
  };

  const printCreatedCards = async () => {
    if (!result?.created?.length) return;
    try {
      const ids = result.created.map((c) => c.id).join(",");
      const res = await api.get("/students/access-cards", { params: { ids }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Kartu_Akses_${result.created.length}_Murid.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Kartu akses diunduh (PDF)");
    } catch {
      toast.error("Gagal membuat kartu akses");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="bulk-student-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Users className="w-5 h-5 text-teal-600" /> Tambah Murid Massal</DialogTitle></DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="bulk-result">
            <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
              <CheckCircle2 className="w-5 h-5" />
              <p className="text-sm font-semibold">{result.count} murid dibuat{result.skipped_count ? `, ${result.skipped_count} dilewati` : ""}. Sandi: semua murid memakai kata sandi yang kamu tentukan.</p>
            </div>
            <div className="border border-slate-200 rounded-xl overflow-hidden max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs">
                  <tr><th className="text-left px-3 py-2">Nama</th><th className="text-left px-3 py-2">Username</th><th className="text-left px-3 py-2">Sandi</th></tr>
                </thead>
                <tbody>
                  {result.created.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100" data-testid={`bulk-row-${c.username}`}>
                      <td className="px-3 py-2 text-slate-700">{c.name}</td>
                      <td className="px-3 py-2 font-mono text-teal-700">@{c.username}</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{c.password}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {result.skipped_count > 0 && (
              <p className="text-xs text-amber-600">Dilewati: {result.skipped.map((s) => `${s.name} (${s.reason})`).join(", ")}</p>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-xl" onClick={printCreatedCards} data-testid="bulk-print-cards-btn"><Download className="w-4 h-4 mr-2" /> Unduh Kartu Akses</Button>
              <Button className="rounded-xl bg-teal-600 hover:bg-teal-700" onClick={() => handleOpenChange(false)} data-testid="bulk-done-btn">Selesai</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Kelas</Label>
                <Select value={kelas} onValueChange={setKelas} disabled={!!wali}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="bulk-kelas-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{[1, 2, 3, 4, 5, 6].map((k) => <SelectItem key={k} value={String(k)}>Kelas {k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rombel</Label>
                <Input value={rombel} onChange={(e) => setRombel(e.target.value)} placeholder="A" maxLength={3} disabled={!!(wali && defaultRombel)} className="mt-1 rounded-xl uppercase" data-testid="bulk-rombel-input" />
              </div>
              <div>
                <Label>Sandi Default</Label>
                <Input value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 rounded-xl" data-testid="bulk-password-input" />
              </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="paste" data-testid="bulk-tab-paste"><ClipboardPaste className="w-4 h-4 mr-2" /> Tempel Nama</TabsTrigger>
                <TabsTrigger value="file" data-testid="bulk-tab-file"><FileUp className="w-4 h-4 mr-2" /> Unggah File</TabsTrigger>
              </TabsList>

              <TabsContent value="paste" className="space-y-3 pt-3">
                <Label>Tempel daftar nama (satu nama per baris)</Label>
                <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder={"Andi Saputra\nBunga Lestari\nCitra Dewi"} className="rounded-xl font-mono text-sm" data-testid="bulk-paste-textarea" />
                <p className="text-xs text-slate-500">{names.length} nama terbaca. Username dibuat otomatis dari nama (tanpa spasi).</p>
                <DialogFooter>
                  <Button onClick={submitPaste} disabled={saving || names.length === 0} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid="bulk-submit-paste-btn">
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Tambahkan {names.length > 0 ? `${names.length} Murid` : ""}
                  </Button>
                </DialogFooter>
              </TabsContent>

              <TabsContent value="file" className="space-y-3 pt-3">
                <div className="flex items-center justify-between">
                  <Label>File Excel (.xlsx) atau CSV</Label>
                  <button onClick={downloadTemplate} className="text-xs text-teal-600 font-medium flex items-center gap-1" data-testid="bulk-template-btn"><Download className="w-3.5 h-3.5" /> Unduh Template</button>
                </div>
                <Input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} className="rounded-xl" data-testid="bulk-file-input" />
                <p className="text-xs text-slate-500">Kolom pertama = Nama. Kolom "Kelas" & "Rombel" opsional (jika ada di file, akan dipakai; jika tidak, pakai pilihan di atas).</p>
                <DialogFooter>
                  <Button onClick={submitFile} disabled={saving || !file} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid="bulk-submit-file-btn">
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Proses File
                  </Button>
                </DialogFooter>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
